<template>
  <div class="row">
    <div class="col-md-12">
      <h1 class="text-center">Refetch Marketing Data</h1>
      <div class="alert alert-info mb-2" role="alert" style="max-width: 600px; margin: auto">
        Multiple apps or days can be chosen. 
      </div>
      <div class="card text-center mb-4" style="max-width: 600px; margin:auto">
        <div class="card-header text-center"></div>
        <div class="card-body">
          <div class="form-group">
            <label>Apps</label>
            <div class="dropdown-check-list">
              <div 
                class="anchor form-control text-left" 
                :class="{ 'active': isOpen }"
                @click="toggleDropdown"
              >
                {{ selectedApps.length ? `${selectedApps.length} apps selected` : 'Choose Apps' }}
              </div>
              <ul class="items" v-show="isOpen">
                <li v-for="app in Object.keys(apps)" :key="app">
                  <label>
                    <input 
                      type="checkbox" 
                      :value="app" 
                      v-model="selectedApps"
                    >
                    {{ apps[app] }}
                  </label>
                </li>
              </ul>
            </div>
          </div>
          <div class="form-group">
            <label>Date Selection</label>
            <div class="date-selection-type mb-2">
              <label class="mr-3">
                <input 
                  type="radio" 
                  v-model="dateSelectionType" 
                  value="single"
                  class="mr-2"
                > Single Date
              </label>
              <label>
                <input 
                  type="radio" 
                  v-model="dateSelectionType" 
                  value="range"
                  class="mr-2"
                > Date Range
              </label>
            </div>
            
            <div v-if="dateSelectionType === 'single'" class="form-group">
              <input
                type="date"
                class="form-control"
                v-model="date"
                :max="maxDate"
              />
            </div>
            
            <div v-else class="date-range">
              <div class="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  class="form-control"
                  v-model="startDate"
                  :max="maxDate"
                />
              </div>
              <div class="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  class="form-control"
                  v-model="endDate"
                  :min="startDate"
                  :max="maxDate"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="card-footer">
          <button
            class="btn btn-primary"
            @click="refetch"
            :disabled="state != 'waiting'"
          >
            {{ uploadBtnText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import notify from "../util/notification";
import tokenManagerService from "../services/tokenManager";

const dateObjToString = (dateObj) => {
  let dd = dateObj.getDate()
  if (dd < 10) dd = "0" + dd
  let mm = dateObj.getMonth() + 1
  if (mm < 10) mm = "0" + mm
  let yyyy = dateObj.getFullYear()
  return yyyy + "-" + mm + "-" + dd
}

const isLocal = window.location.href.indexOf("localhost") >= 0;
const backendUrl = isLocal ? "http://localhost:5002" : "";

const fetchMarketingData = async (apps, startDate, endDate, token) => {
  const response = await axios.post(
    `${backendUrl}/fetch-marketing-data`,
    {
      apps: Array.isArray(apps) ? apps : [apps],
      startDate: startDate,
      endDate: endDate,
    },
    {
      headers: {
        authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export default {
  name: 'CostRefetchPage',
  data() {
    return {
      selectedApps: [],
      date: undefined,
      startDate: undefined,
      endDate: undefined,
      dateSelectionType: 'single',
      state: "waiting",
      isOpen: false,
    };
  },
  computed: {
    apps() {
      return this.$store.getters.allApps
    },
    uploadBtnText() {
      if (this.state == "waiting") {
        return "Start Refetch";
      } else if (this.state == "fetching") {
        return "Fetching New Data...";
      } else if (this.state == "fetched") {
        return "Fetched New Data";
      } else {
        return "unexpected state";
      }
    },
    
    maxDate() {
      const date = new Date();
      return dateObjToString(date);
    },
    isValidDateSelection() {
      if (this.dateSelectionType === 'single') {
        return !!this.date;
      }
      
      if (!this.startDate || !this.endDate) {
        return false;
      }

      // Check if date range is valid and not exceeding 1 month
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);
      
      if (start > end) {
        return false;
      }

      return true;
    }
  },
  methods: {
    async refetch() {
      if (this.selectedApps.length === 0) {
        notify("Please choose at least one app!", "error");
        return;
      }

      if (!this.isValidDateSelection) {
        notify("Please select valid date(s)!", "error");
        return;
      }

      const today = new Date();
      const startDate = this.dateSelectionType === 'single' ? this.date : this.startDate;
      const endDate = this.dateSelectionType === 'single' ? this.date : this.endDate;

      // Check if the selected date(s) are in the future
      if (new Date(startDate) > today || new Date(endDate) > today) {
        notify("Selected date(s) cannot be in the future!", "error");
        return;
      }

      this.state = "fetching";
      const days = this.dateSelectionType === 'single' ? 1 : Math.ceil((new Date(this.endDate) - new Date(this.startDate)) / (1000 * 60 * 60 * 24)) + 1;
      const estimatedTime = days <= 10 ? "around 2 minutes" : `around ${Math.ceil(days/10) * 2} minutes`;
      notify(`Refetch started, this will take ${estimatedTime}`, "info");
      const token = await tokenManagerService.getValidToken();

      try {
        const response = await fetchMarketingData(this.selectedApps, startDate, endDate, token);
        notify(`${response.message}`, "info");
        this.state = 'fetched';
      } catch (error) {
        notify(`Error: ${error.message}`, "error");
        this.state = 'waiting';
      }
    },
    getDateRange(start, end) {
      const dates = [];
      const currentDate = new Date(start);
      const endDate = new Date(end);

      while (currentDate <= endDate) {
        dates.push(dateObjToString(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return dates;
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
  },
  mounted() {
    document.addEventListener('click', (e) => {
      const dropdown = document.querySelector('.dropdown-check-list');
      if (dropdown && !dropdown.contains(e.target)) {
        this.isOpen = false;
      }
    });
  },
  beforeDestroy() {
    document.removeEventListener('click');
  }
};
</script>

<style scoped>
.dropdown-check-list {
  position: relative;
}

.anchor {
  cursor: pointer;
  position: relative;
  background-color: #343a40;
  color: white;
  border-color: #495057;
}

.anchor:after {
  content: "";
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid white;
  position: absolute;
  right: 10px;
  top: 45%;
}

.anchor.active:after {
  border-top: none;
  border-bottom: 5px solid white;
}

.items {
  position: absolute;
  background: #343a40;
  color: white;
  border: 1px solid #495057;
  border-radius: 4px;
  list-style-type: none;
  padding: 0;
  margin: 0;
  width: 100%;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
}

.items li {
  padding: 8px 12px;
  cursor: pointer;
  text-align: left;
}

.items li:hover {
  background-color: #495057;
}

.items label {
  display: flex;
  align-items: center;
  margin: 0;
  cursor: pointer;
  width: 100%;
}

.items input[type="checkbox"] {
  margin-right: 8px;
}

.items::-webkit-scrollbar {
  width: 8px;
}

.items::-webkit-scrollbar-track {
  background: #343a40;
}

.items::-webkit-scrollbar-thumb {
  background: #495057;
  border-radius: 4px;
}

.items::-webkit-scrollbar-thumb:hover {
  background: #6c757d;
}

.date-selection-type {
  text-align: left;
}

.date-range {
  display: flex;
  gap: 1rem;
}

.date-range .form-group {
  flex: 1;
}

input[type="date"] {
  background-color: #343a40;
  color: white;
  border-color: #495057;
  cursor: pointer;
}

input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
  cursor: pointer;
}

input[type="radio"] {
  cursor: pointer;
}

label {
  cursor: pointer;
}
</style>
